import { useContext, useMemo } from "react";
import * as yup from "yup";
import { GeaoContext } from '../../../GeaoContextProvider';
import { ClientConfigurationContext } from "../../../../../context/ClientConfigurationContextProvider";
import { streetAddressLineOneValidationSchema, streetAddressLineTwoValidationSchema, zipCodeValidationSchema, cityValidationSchema, stateValidationSchema } from "../../../../AddressFields";
import { EMAIL_WITH_TLD_REGEX } from "../../../../../constants/ValidationConstants";
import { isCobrandedSubAgency } from "../../../mappers/InterviewMapper";
import useCobrandedAgency from "../../../hooks/useCobrandedAgency";

/**
 * Custom hook that creates a Yup validation schema for Agency Office Information form
 * with complex conditional validation logic based on form field dependencies.
 * 
 * @returns {yup.ObjectSchema} Validation schema for the form fields
 */
export default function useValidationSchema() {
    const { agencyNpnOptionalStates } = useContext(ClientConfigurationContext);
    const { agencyInterview } = useContext(GeaoContext);
    const { isCobrandedAgency } = useCobrandedAgency();

    /**
     * Determines if all appointed states are in the optional NPN states list.
     * This is used to conditionally make the Agency NPN field optional when all appointed states
     * don't require NPN validation.
     * 
     * @returns {boolean} True if all appointed states are in the optional states list, false otherwise
     * 
     * @description
     * This function:
     * 1. Retrieves the appointed states from the agency interview answers
     * 2. Compares them against the list of states where NPN is optional
     * 3. Returns true only if ALL appointed states are in the optional list
     * 
     * If any appointed state requires NPN validation, this function returns false,
     * making the NPN field required in the validation schema.
     */
    const areAllAppointedStatesOptional = () => {
        const appointedStatesAnswer = agencyInterview?.questionAnswers?.GeicoAppointedStates?.answer;

        if (!appointedStatesAnswer || !agencyNpnOptionalStates || agencyNpnOptionalStates.length === 0) {
            return false;
        }

        // Split the comma-separated state abbreviations and trim whitespace
        const appointedStates = appointedStatesAnswer.split(',').map(state => state.trim());

        // Check if all appointed states are in the optional states list
        return appointedStates.every(state => agencyNpnOptionalStates.includes(state));
    };

    return useMemo(() => {
        return yup.object({
            dba: yup.string().optional(),
            agencyNpn: yup.string().when(["w9RegistrationAsIndividual", "fein"], {
                is: (w9RegistrationAsIndividual, fein) => {
                    // If all appointed states are optional for NPN, don't require it
                    if (areAllAppointedStatesOptional()) {
                        return false;
                    }
                    // Otherwise, required for non-sole proprietors and those with an FEIN.
                    return !w9RegistrationAsIndividual || fein !== "";
                },
                then: schema => schema.required("Required.")
            }),
            yearsInBusiness: yup.number().typeError("Must be a number.").required("Required."),
            w9RegistrationAsIndividual: yup.bool().typeError("Required.").required("Required."),
            feinOrSsn: yup.string().required("Required."),
            fein: yup.string().when("feinOrSsn", {
                is: "fein",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            ssn: yup.string().when("feinOrSsn", {
                is: "ssn",
                then: schema => schema.required("Required.").min(9, "Please enter a valid SSN."),
                otherwise: schema => schema.nullable().optional()
            }),
            physicalAddressLine1: yup.string().when("physicalSameAsPrincipalResidential", {
                is: false,
                then: schema => streetAddressLineOneValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            physicalAddressLine2: streetAddressLineTwoValidationSchema,
            physicalAddressZipCode: yup.string().when("physicalSameAsPrincipalResidential", {
                is: false,
                then: schema => zipCodeValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            physicalAddressCity: yup.string().when("physicalSameAsPrincipalResidential", {
                is: false,
                then: schema => cityValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            physicalAddressState: yup.string().when("physicalSameAsPrincipalResidential", {
                is: false,
                then: schema => stateValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            mailingAddressLine1: yup.string().when(["mailingSameAsPrimaryPhysical"], {
                is: (mailingSameAsPrimaryPhysical) => !mailingSameAsPrimaryPhysical,
                then: schema => streetAddressLineOneValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            mailingAddressLine2: streetAddressLineTwoValidationSchema,
            mailingAddressZipCode: yup.string().when(["mailingSameAsPrimaryPhysical"], {
                is: (mailingSameAsPrimaryPhysical) => !mailingSameAsPrimaryPhysical,
                then: schema => zipCodeValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            mailingAddressCity: yup.string().when(["mailingSameAsPrimaryPhysical"], {
                is: (mailingSameAsPrimaryPhysical) => !mailingSameAsPrimaryPhysical,
                then: schema => cityValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            mailingAddressState: yup.string().when(["mailingSameAsPrimaryPhysical"], {
                is: (mailingSameAsPrimaryPhysical) => !mailingSameAsPrimaryPhysical,
                then: schema => stateValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            affiliatedWithTop100: yup.bool().required("Required."),
            firmNetworkOrAggregator: yup.string().transform((value) => {
                // Transform array to first string element if it's an array
                if (Array.isArray(value) && value.length > 0) {
                    return value[0];
                }
                return "";
            }).when("affiliatedWithTop100", {
                is: true,
                then: schema => schema.nonNullable("Required.").required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            firmNetworkAggregatorCommissionPayments: yup.mixed().transform((_, originalValue) => originalValue == "true").when("affiliatedWithTop100", {
                is: true,
                then: schema => schema.typeError("Required.").required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            agencyGroupName: yup.string().when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments"], {
                is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments) => affiliatedWithTop100 && firmNetworkAggregatorCommissionPayments,
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            agencyGroupEmailAddress: yup.string()
                .email("Please enter a valid email address.")
                .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
                .when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments"], {
                    is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments) => affiliatedWithTop100 && firmNetworkAggregatorCommissionPayments,
                    then: schema => schema.required("Required."),
                    otherwise: schema => schema.nullable().optional()
                }),
            agencyGroupFEIN: yup.string().when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments"], {
                is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments) => affiliatedWithTop100 && firmNetworkAggregatorCommissionPayments,
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            agencyCommissionPayments: yup.bool().when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments"], {
                is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments) => affiliatedWithTop100 && !firmNetworkAggregatorCommissionPayments,
                then: schema => schema.typeError("Required.").required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            parentAgencyName: yup.string().when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments", "agencyCommissionPayments"], {
                is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments, agencyCommissionPayments) => (affiliatedWithTop100 && !firmNetworkAggregatorCommissionPayments && !agencyCommissionPayments) || (!affiliatedWithTop100 && !agencyCommissionPayments),
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            parentAgencyEmailAddress: yup.string()
                .email("Please enter a valid email address.")
                .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
                .when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments", "agencyCommissionPayments"], {
                    is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments, agencyCommissionPayments) => (affiliatedWithTop100 && !firmNetworkAggregatorCommissionPayments && !agencyCommissionPayments) || (!affiliatedWithTop100 && !agencyCommissionPayments),
                    then: schema => schema.required("Required."),
                    otherwise: schema => schema.nullable().optional()
                }),
            parentAgencyFein: yup.string().when(["affiliatedWithTop100", "firmNetworkAggregatorCommissionPayments", "agencyCommissionPayments"], {
                is: (affiliatedWithTop100, firmNetworkAggregatorCommissionPayments, agencyCommissionPayments) => (affiliatedWithTop100 && !firmNetworkAggregatorCommissionPayments && !agencyCommissionPayments) || (!affiliatedWithTop100 && !agencyCommissionPayments),
                then: schema => schema.required("Please enter a parent agency FEIN."),
                otherwise: schema => schema.nullable().optional()
            }),
            centersOfOperationOutsidePrimaryLocation: yup.bool().required("Required."),
            numberOfCentersOfOperationUnderSameEin: yup.string().when("centersOfOperationOutsidePrimaryLocation", {
                is: true,
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchName: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchAddressLine1: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => streetAddressLineOneValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branchAddressLine2: streetAddressLineTwoValidationSchema,
            branchAddressZipCode: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => zipCodeValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branchAddressCity: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => cityValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branchAddressState: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => stateValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branchContactFirstName: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchContactLastName: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchContactEmail: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema
                    .email("Please enter a valid email address.")
                    .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
                    .required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchContactRole: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branchPhone: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && (numberOfCentersOfOperationUnderSameEin === "one" || numberOfCentersOfOperationUnderSameEin === "two"),
                then: schema => schema.min(10, "Phone number must be 10 digits.").required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2Name: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2AddressLine1: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => streetAddressLineOneValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branch2AddressLine2: streetAddressLineTwoValidationSchema,
            branch2AddressZipCode: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => zipCodeValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branch2AddressCity: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => cityValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branch2AddressState: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => stateValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            branch2ContactFirstName: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2ContactLastName: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2ContactEmail: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema
                    .email("Please enter a valid email address.")
                    .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
                    .required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2ContactRole: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            branch2Phone: yup.string().when(["centersOfOperationOutsidePrimaryLocation", "numberOfCentersOfOperationUnderSameEin"], {
                is: (centersOfOperationOutsidePrimaryLocation, numberOfCentersOfOperationUnderSameEin) => centersOfOperationOutsidePrimaryLocation && numberOfCentersOfOperationUnderSameEin === "two",
                then: schema => schema.min(10, "Phone number must be 10 digits.").required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            entityType: yup.string().when([], {
                is: () => isCobrandedAgency,
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            partnershipType: yup.string().when("entityType", {
                is: entityType => isCobrandedAgency && entityType === "Partnership",
                then: schema => schema.required("Required."),
                otherwise: schema => schema.nullable().optional()
            }),
            entityFiledState: yup.string().when([], {
                is: () => isCobrandedAgency,
                then: schema => stateValidationSchema,
                otherwise: schema => schema.nullable().optional()
            }),
            // Cobranded commissions acknowledgement - only required for cobranded sub-agencies
            cobrandedCommissionsAgreement: yup.bool().when([], {
                is: () => isCobrandedSubAgency(agencyInterview),
                then: schema => schema.oneOf([true], "Please agree to the above Commissions Acknowledgement Statement.").required("Please agree to the above Commissions Acknowledgement Statement."),
                otherwise: schema => schema.nullable().optional()
            })
        });
    }, [agencyNpnOptionalStates, agencyInterview, isCobrandedAgency]);
};